import { useLazyQuery } from "@apollo/client/react"; 
import { useEffect, useMemo, useRef, useState } from 'react';
import 'moment/locale/es';
import moment from 'moment';
import { useNavigate, useLocation } from 'react-router-dom';
import { FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { GET_DELEGACIONES_BY_USUARIO } from '../../../data/graphQL/queries/PacienteProbable/getAllDelegacionesByUsuario';
import { GET_USUARIO_DELEGACION_ORIGEN } from '../../../data/graphQL/queries/usuarios/getUsuarioDelegacionOrigen';
import { GET_ADMIN_INDICACIONES } from '../../../data/graphQL/queries/AdminIndicacion/getAdminIndicaciones';
import { adaptDelegacionArray } from '../../../adapters/delegacionAdapter';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { CustomChip } from '../../../components/chip/Chip';
import { PopoverModal } from '../../../components/popoverModal/PopoverModal';
import './adminIndicacionAll.scss';
import { CustomButton } from '../../../components/customButton/CustomButton';
import { GET_ESTADOS_INDICACION_APROBACION } from '../../../data/graphQL/queries/AdminIndicacion/getEstadosIndicacionAprobacion';
import { adaptEstadosSolicitudIndicacionArray } from '../../../adapters/estadoSolicitudIndicacionAdapter';
import { usePersistFilters } from '../../../hooks/usePersistFilters';

export const AdminIndicacionAll = () => {
  const storageId = sessionStorage.getItem('idUsuario');
  const navigate = useNavigate();

  const [allowedWrite, setAllowedWrite] = useState(false);

  const { filters, handleChangeFilters } = usePersistFilters({
      idDelegacion: 0,
      idEstado: 1, //Pendiente
      txtFiltro: ''
    }, "adminIndicacionGrillaFilters"); 

  const [delegaciones, setDelegaciones] = useState([]);
  const [delegacionOrigen, setDelegacionOrigen] = useState(0);
  const [indicaciones, setIndicaciones] = useState([]);
  const [datosOriginal, setDatosOriginal] = useState([]);
  const [estados, setEstados] = useState([]);

  const [getEstados, { data: dataEstados, loading: estadosLoading }] = useLazyQuery(GET_ESTADOS_INDICACION_APROBACION, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataEstados) return;
    setEstados(adaptEstadosSolicitudIndicacionArray(dataEstados));
  }, [dataEstados]);

  const location = useLocation();
  const rutasStorage = localStorage.getItem('navigationRoutes');

  const ESTADO_PENDIENTE = 1;
  const ESTADO_APROBADO = 2;
  const ESTADO_NUEVO_CONTRATO = 3;
  const ESTADO_RECHAZADO = 4;

  useEffect(() => {
    if (rutasStorage) {
      const rutas = JSON.parse(rutasStorage);
      const rutaQueSirve = location.pathname;

      setAllowedWrite(rutas?.find((ruta) => ruta.aplicaciones.find((app) => app.url == rutaQueSirve)
      )?.aplicaciones?.find((app) => app.url == rutaQueSirve).escritura);
    }
  }, [rutasStorage]);

  useEffect(() => {
    if (!delegacionesLoading)
      getDelegaciones({ variables: { idUsuario: JSON.parse(storageId, 10) } });
    if (!estadosLoading)
      getEstados();
  }, []);

  const [getDelegaciones, { data: dataDelegaciones, loading: delegacionesLoading }] = useLazyQuery(GET_DELEGACIONES_BY_USUARIO, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataDelegaciones) return;
    setDelegaciones(adaptDelegacionArray(dataDelegaciones));
  }, [dataDelegaciones]);

  useEffect(() => {
    if (delegaciones != null && !delegacionOrigenLoading)
      getDelegacionOrigen({ variables: { idUsuario: JSON.parse(storageId, 10) } });
  }, [delegaciones]);

  useEffect(() => {
    if (!indicacionesLoading && filters?.idDelegacion > 0)
      getAllIndicaciones({ variables: { idDelegacion: filters?.idDelegacion, idEstado: filters?.idEstado } });
  }, [filters]);

  const [getDelegacionOrigen, { data: dataDelegacionOrigen, loading: delegacionOrigenLoading }] = useLazyQuery(GET_USUARIO_DELEGACION_ORIGEN, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataDelegacionOrigen || delegacionOrigenLoading) return;
    if (dataDelegacionOrigen.usuarioFull.success) {
      const deleg = parseInt(dataDelegacionOrigen.usuarioFull.valorEsperado.delegacionOrigen, 10);
      setDelegacionOrigen(deleg);
    }
  }, [dataDelegacionOrigen]);

  const getChipEstado = (estado) => {
    switch (estado) {
      case ESTADO_PENDIENTE:
        return (
          <CustomChip label='Pendiente' isYellow title='Pendiente' />);

      case ESTADO_APROBADO:
        return (
          <CustomChip label='Aprobado' isGreen title='Aprobado' />);

      case ESTADO_NUEVO_CONTRATO:
        return (
          <CustomChip label='Nvo. Contrato' isBlue title='Nuevo Contrato' />);

      case ESTADO_RECHAZADO:
        return (
          <CustomChip label='Rechazado' isRed title='Rechazado' />);

      default: return;
    }
  };

  const [getAllIndicaciones, { data: dataIndicaciones, loading: indicacionesLoading }] = useLazyQuery(GET_ADMIN_INDICACIONES, {
    fetchPolicy: 'no-cache'
  });

  const handleClickBuscar = () => {
    if (filters?.idDelegacion > 0)
      getAllIndicaciones({ variables: { idDelegacion: filters?.idDelegacion, idEstado: filters?.idEstado } });
  };

  useEffect(() => {
    if (!dataIndicaciones || indicacionesLoading) return;
    setIndicaciones(dataIndicaciones?.adminIndicaciones?.valorEsperado);
    setDatosOriginal(dataIndicaciones?.adminIndicaciones?.valorEsperado);
  }, [dataIndicaciones]);

  const handleClickAction = (row, edit) => {
    const ruta = location.pathname;
    navigate(ruta + '/detalle', {
      state: {
        dataRegistro: row,
        edit: edit
      }
    });
  };

    useEffect(() => {
      handleTextFilter();
    }, [datosOriginal]);

  const handleTextFilter = () => {
    const inputBuscar = filters?.txtFiltro?.toUpperCase()?.trim();

    if (inputBuscar?.length >= 2) {
      setIndicaciones(datosOriginal?.filter((datoOriginal) => {
        const contratoBuscar = datoOriginal.idEvaluacion.toString();
        const nombrePacienteBuscar = datoOriginal.paciente.toUpperCase();
        const clienteBuscar = datoOriginal.cliente.toUpperCase();
        const programaBuscar = datoOriginal.programa.toUpperCase();
        const planBuscar = datoOriginal.planCuidado != '' && datoOriginal.planCuidado != null ? datoOriginal.planCuidado.toUpperCase() : '';

        return (
          contratoBuscar.includes(inputBuscar) ||
          nombrePacienteBuscar.includes(inputBuscar) ||
          clienteBuscar.includes(inputBuscar) ||
          programaBuscar.includes(inputBuscar) ||
          planBuscar.includes(inputBuscar));

      }));
    } else {
      setIndicaciones(datosOriginal);
    }
  };

  const initialColumns = [
    {
      headerName: '',
      field: 'action',
      flex: 0.04,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return (
          <>
            <PopoverModal>
              <div className='popover-modal-content'>
                <div
                  onClick={() => handleClickAction(row, false)}
                  className='item-option clickable-option'
                  aria-hidden='true'
                >
                  <VisibilityIcon className='edit-icon' />
                  <span className='toggle-action'>Ver</span>
                </div>
                {
                  allowedWrite && ( (row?.permisoMedico && row.idEstadoIndicacionAprobacionMedico == ESTADO_PENDIENTE) ||
                                  (!row?.permisoMedico && row.idEstadoIndicacionAprobacionComercial == ESTADO_PENDIENTE)) &&
                                  (moment(row?.fechaHastaContrato).isSameOrAfter(moment(), 'day')) &&
                  <div
                    onClick={() => handleClickAction(row, true)}
                    className='item-option clickable-option'
                    aria-hidden='true'
                  >
                    <EditIcon className='edit-icon' />
                    <span className='toggle-action'>Editar</span>
                  </div>
                }
              </div>
            </PopoverModal>
          </>);

      }
    },
    {
      field: 'idEvaluacion',
      headerName: 'Contrato',
      flex: 0.13
    },
    {
      field: 'paciente',
      headerName: 'Paciente',
      flex: 0.20
    },
    {
      field: 'cliente',
      headerName: 'Cliente',
      flex: 0.20
    },
    {
      field: 'programa',
      headerName: 'Prog.',
      flex: 0.1
    },
    {
      field: 'fechaSolicitud',
      headerName: 'F. solicitud',
      flex: 0.15,
      type: 'date'
    },
    {
      field: 'planCuidado',
      headerName: 'Plan',
      flex: 0.18
    },
    {
      field: 'fechaContrato',
      headerName: 'F. contrato',
      flex: 0.25
    },
    {
      field: 'fechaIndicacionActual',
      headerName: 'F. indicación actual',
      flex: 0.25
    },
    {
      field: 'fechaIndicacionNueva',
      headerName: 'F. indicación nueva',
      flex: 0.25
    },
    {
      field: 'estadoAprobacionMedico',
      headerName: 'Médico',
      flex: 0.15,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return getChipEstado(row.idEstadoIndicacionAprobacionMedico);
      }
    },
    {
      field: 'estadoAprobacionComercial',
      headerName: 'Comercial',
      flex: 0.17,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return getChipEstado(row.idEstadoIndicacionAprobacionComercial);
      }
    }];


  const columns = useMemo(() => generateColumnsDefaultConfiguration(initialColumns), [indicaciones]);

  return (
    <>
      <div>
        <Grid
          container
          spacing={2}
          sx={{ marginBottom: '25px' }}>

          <Grid size={{ md: 2.5 }}>
            <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size="small">
              <InputLabel id="select-delegacion">Delegación</InputLabel>
              <Select
                labelId="select-delegacion"
                options={delegaciones}
                label='Delegación'
                onChange={(e) => {
                  handleChangeFilters('idDelegacion', e.target.value);
                }}
                value={filters?.idDelegacion}
                name='idDelegacion'>

                {delegaciones && delegaciones.map((delegacion) => {
                  return <MenuItem key={delegacion.id} value={delegacion.id}> {delegacion.displayName} </MenuItem>;
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ md: 2.5 }}>
            <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size="small">
              <InputLabel id="select-estado">Estado</InputLabel>
              <Select
                labelId="select-estado"
                options={estados}
                label='Estado'
                onChange={(e) => {
                  handleChangeFilters('idEstado', e.target.value);
                }}
                value={filters?.idEstado}
                name='idEstado'>

                {estados && estados.map((estado) => {
                  return <MenuItem key={estado.id} value={estado.id}> {estado.displayName} </MenuItem>;
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 24, sm: 6, md: 3, lg: 3 }}>
            <TextField
              label="Filtrar"
              name="txtFiltro"
              size="small"
              type="text"
              variant="outlined"
              value={filters?.txtFiltro}
              onChange={(e) => handleChangeFilters('txtFiltro', e.target.value)}
              onKeyUp={handleTextFilter}
              sx={{ backgroundColor: 'white' }}
              InputProps={{
                endAdornment:
                  <InputAdornment position="end">
                    <SearchIcon
                      onClick={handleTextFilter}
                      sx={{ cursor: 'pointer' }} />

                  </InputAdornment>

              }} />

          </Grid>
          <Grid sx={{ textAlign: 'right' }} size={{ md: 1.2 }}>
            <CustomButton
              variant='contained'
              className='margin-right'
              onClick={handleClickBuscar}
              size='small'>

              Buscar
            </CustomButton>
          </Grid>
        </Grid>

        <DataGridGenerico
          columns={columns}
          rows={indicaciones}
          toolbar={false}
          maxWidth={columns.reduce(
            (total, column) => total + (column.width || 0),
            0
          )}
          loading={indicacionesLoading} />

      </div>
    </>);

};